/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { getReportColumns, Report, columnDisplayNames } from "@/Components/reports_table/columns";
import { Card } from "../Components/ui/card";
import { DataTableWrapper } from "@/Components/datatable/data-table";
import { useState } from "react";
import { PDFViewer } from "@react-pdf/renderer";
import StatusReportPDF from "../Components/report_pdf/status_report";
import { StatusReport } from "./StatusReport";
import React from "react";
import { AppContext } from "@/App";
import { Charter } from "@/Components/Charter/Charter";
import { ProjectChangeRequest } from "@/Components/Charter/projectChange_table/columns";
import CharterReportPDF from "@/Components/report_pdf/charter_report";
import Confirmation from "@/hooks/confirmation";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/Components/ui/dialog";
import { ShowFiltersIcon } from "@/Components/ui/Icons/TooltipIcon";
import { UpcomingDeliverable } from "@/Components/UpcomingDeliverables_table/columns";
import { useResponsiveZoom } from "@/hooks/use-responsive-zoom";
import { useIsMobile } from "@/hooks/use-mobile";

export type ReportColumnVisibility = {
  reportId: boolean;
  reportType: boolean;
  name: boolean;
  date: boolean;
}

export type CharterReport = {
  projectCharter: Charter;
  deliverables: UpcomingDeliverable[];
  changeRequests: ProjectChangeRequest[];
}

function Reports() {
  const app = React.useContext(AppContext);
  const zoomLevel = useResponsiveZoom();
  const isMobile = useIsMobile();

  const [showTableFilters, setShowTableFilters] = useState(false);

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dialogTitle, setDialogTitle] = useState("Create New Risk");
  const [dialogContent, setDialogContent] = useState<JSX.Element | null>(null);

  const [reportTableContent, setReportTableContent] = React.useState<Report[]>([]);

  const [statusReportData, setStatusReportData] = useState<StatusReport>();
  const [charterReportData, setCharterReportData] = useState<CharterReport>();
  const [selectedReportType, setSelectedReportType] = useState<"status" | "charter">("status");
  const [loading, setLoading] = useState<boolean>(true);

  React.useEffect(() => {
    async function fetchReportData() {
      setLoading(true);
      document.body.style.cursor = 'wait';
      const response = await fetch(`Reports/ReportsByProject/${app.selectedProject?.id}`);
      if (response.ok) {
        const data = await response.json();
        const formattedData = data.map((item: any) => ({
          reportId: item.reportId + item.reportType[0].toUpperCase(),
          reportType: item.reportType,
          name: item.name,
          date: item.date,
        }));
        setReportTableContent(formattedData);
        setLoading(false);
        await selectNextReport(formattedData);
      } else {
        console.error("Failed to fetch report data");
      }
      document.body.style.cursor = 'default';
    }
    fetchReportData();
  }, [app.selectedProject]);

  async function selectNextReport(tableData: Report[]) {
    if (tableData.length > 0) {
      const id = Number(tableData[0].reportId.slice(0, -1));
      if (tableData[0].reportType === "status") {
        await fetchStatusReportDataById(id);
        setSelectedReportType("status");
      } else {
        await fetchCharterReportById(id);
        setSelectedReportType("charter");
      }
    }
    else {
      setStatusReportData(undefined);
      setCharterReportData(undefined);
    }
  }

  function confirmDeleteReports(reportIds: string[]) {
    setDialogTitle(reportIds.length > 1 ? "Are you sure you want to delete these reports?" : "Are you sure you want to delete this report?");
    setDialogContent(<Confirmation onConfirm={() => { deleteReports(reportIds); setDialogOpen(false); }} onCancel={() => setDialogOpen(false)} confirmColour="bg-chart-red" />);
    setDialogOpen(true);
  }

  function loadColumnVisibility(): ReportColumnVisibility {
    const localStorageValue = localStorage.getItem(app.applicationUser?.id + "_Reports_columnVisibility");
    if (localStorageValue) {
      const visibilityValues: { columnId: string, isVisible: boolean }[] = JSON.parse(localStorageValue);
      return {
        reportId: visibilityValues.find((v) => v.columnId === "reportId")?.isVisible ?? false,
        reportType: visibilityValues.find((v) => v.columnId === "reportType")?.isVisible ?? false,
        name: visibilityValues.find((v) => v.columnId === "name")?.isVisible ?? true,
        date: visibilityValues.find((v) => v.columnId === "date")?.isVisible ?? true,
      }
    }
    return {
      reportId: false,
      reportType: false,
      name: true,
      date: true,
    }
  }

  async function fetchCharterReportById(id: number) {
    setCharterReportData(undefined); // Clear previous data
    document.body.style.cursor = 'wait';
    const response = await fetch(`Reports/CharterReportById/${id}`);
    if (response.ok) {
      const data = await response.json();
      const reportData: CharterReport = {
        projectCharter: {
          projectId: data.ProjectCharter.ProjectId,
          projectName: data.ProjectCharter.ProjectName,
          objective: data.ProjectCharter.Objective,
          inScope: data.ProjectCharter.InScope,
          outOfScope: data.ProjectCharter.OutOfScope,
          criticalSuccessFactors: data.ProjectCharter.CriticalSuccessFactors,
          submittedForApproval: data.ProjectCharter.SubmittedForApproval,
          submissionDate: data.ProjectCharter.SubmissionDate,
          submissionSeen: data.ProjectCharter.SubmissionSeen,
          sponsorApproval: data.ProjectCharter.SponsorApproval,
          sponsorApprovalDate: data.ProjectCharter.SponsorApprovalDate,
          sponsorId: data.ProjectCharter.SponsorId,
          sponsorName: data.ProjectCharter.SponsorName,
          sponsors: data.ProjectCharter.Sponsors.map((s: any) => ({
            id: s.Id,
            userName: s.UserName,
            forename: s.Forename,
            surname: s.Surname,
          })),
          projectManagers: data.ProjectCharter.ProjectManagers.map((pm: any) => ({
            id: pm.Id,
            userName: pm.UserName,
            forename: pm.Forename,
            surname: pm.Surname,
          })),
          teamMembers: data.ProjectCharter.TeamMembers.map((tm: any) => ({
            id: tm.Id,
            userName: tm.UserName,
            forename: tm.Forename,
            surname: tm.Surname,
          })),
          majorMilestones: data.ProjectCharter.MajorMilestones.map((ms: any) => ({
            workPackageId: ms.WorkPackageId,
            workPackageName: ms.WorkPackageName,
            tasks: ms.Tasks.map((task: any) => ({
              id: task.Id,
              name: task.Name,
              finishDate: task.FinishDate,
            }))
          })),
        },
        deliverables: data.Deliverables.map((d: any) => ({
          workPackage: d.workPackage,
          deliverable: d.deliverable,
          plannedFinish: d.plannedFinish,
          owner: d.owner,
        })),
        changeRequests: data.ChangeRequests.map((cr: any) => ({
          projectId: cr.ProjectId,
          projectChangeRequestId: cr.ProjectChangeRequestId,
          description: cr.Description,
          workPackageId: cr.WorkPackageId,
          workPackage: cr.WorkPackage,
          workstreamId: cr.WorkstreamId,
          workstream: cr.Workstream,
          reviewStatus: cr.ReviewStatus,
          status: cr.Status,
          date: cr.Date,
          rating: cr.Rating,
          closedDate: cr.ClosedDate,
          raisedBy: cr.RaisedBy,
          userId: cr.UserId,
          decisionMaker: cr.DecisionMaker,
          dueDate: cr.DueDate,
          owner: cr.Owner,
          notes: cr.Notes,

        })),
      };
      setCharterReportData(reportData);
    } else {
      app.showActionNotification("Error", "Failed to fetch charter report data", "destructive");
    }
    document.body.style.cursor = 'default';
  }


  async function fetchStatusReportDataById(id: number) {
    setStatusReportData(undefined); // Clear previous data
    document.body.style.cursor = 'wait';
    const response = await fetch(`Reports/StatusReportById/${id}`);
    if (response.ok) {
      const data = await response.json();
      const reportData: StatusReport = {
        statusReportId: data.StatusReportId,
        workstreamId: data.WorkstreamId,
        workstreamName: data.WorkstreamName,
        date: data.Date,
        estimatedCloseDate: data.EstimatedCloseDate,
        health: data.Health,
        activitiesSince: data.ActivitiesSince,
        nextSteps: data.NextSteps,
        workPackages: [],
        riskIssues: data.RiskIssues,
        supportRequests: data.SupportRequests,
      };
      reportData.health = {
        workstreamId: data.Health.WorkstreamId ?? 0,
        financial: data.Health.Financial,
        schedule: data.Health.Schedule,
        scope: data.Health.Scope,
        resources: data.Health.Resources,
        other: data.Health.Other,
        overall: data.Health.Overall,
      };

      reportData.workPackages = data.WorkPackages.map((wp: any) => ({
        workPackageId: wp.WorkPackageId,
        name: wp.Name,
        startDate: wp.StartDate,
        finishDate: wp.FinishDate,
        tasks: wp.Tasks.map((task: any) => ({
          taskId: task.TaskId,
          name: task.Name,
          finishDate: task.FinishDate,
          completed: task.Completed,
          completedDate: task.CompletedDate,
          status: task.Status,
          displayOnCharter: task.DisplayOnCharter,
          displayOnStatusReport: task.DisplayOnStatusReport,
        }))
      }));
      reportData.riskIssues = data.RiskIssues.map((ri: any) => ({
        id: ri.Id,
        description: ri.Description,
        mitigation: ri.Mitigation,
        rating: ri.Rating,
        isRisk: ri.IsRisk,
      }));
      reportData.supportRequests = data.SupportRequests.map((sr: any) => ({
        supportRequestId: sr.SupportRequestId,
        description: sr.Description,
        date: sr.Date,
      }));
      setStatusReportData(reportData);
    } else {
      app.showActionNotification("Error", "Failed to fetch report data by ID", "destructive");
    }
    document.body.style.cursor = 'default';
  }

  const groupActions = [
    {
      name: "Delete",
      action: (selectedRows: any[]) => {
        confirmDeleteReports(selectedRows.map((row) => row.reportId));
      },
    }
  ];

  function handleReportClick(id: string, reportType: "status" | "charter") {
    // strip the reportType from the id ready to send to the API
    const reportId = Number(id.slice(0, -1));
    if (reportType === "charter") {
      fetchCharterReportById(reportId);
      setSelectedReportType("charter");
    } else {
      fetchStatusReportDataById(reportId);
      setSelectedReportType("status");
    }

    if (isMobile) {
      showPDFDialog();
    }
  }

  function showPDFDialog() {
    setDialogTitle("Report Viewer");
    setDialogContent(
      <div style={{ width: isMobile ? "90vw" : "60vw", height: isMobile ? "80vh" : "90vh" }}>
        <PDFViewer style={{ width: "100%", height: "100%" }}>
          {selectedReportType === "status" && statusReportData ? (
            <StatusReportPDF reportData={statusReportData} projectType={app.selectedProject?.projectTypeId!} />
          ) : selectedReportType === "charter" && charterReportData ? (
            <CharterReportPDF reportData={charterReportData} projectTypeId={app.selectedProject?.projectTypeId!} />
          ) : (
            <div className="flex items-center justify-center h-full">
              <p>Select a report to view</p>
            </div>
          )}
        </PDFViewer>
      </div>
    );
    setDialogOpen(true);
  }

  function deleteReports(ids: string[]) {
    if (ids.length === 0) {
      app.showActionNotification("Action Disallowed", "No reports selected", "default");
      return;
    }

    // check if any of the selected reports are charter reports
    const includesCharterReport = reportTableContent.some((report) => ids.includes(report.reportId) && report.reportType === "charter");
    if (includesCharterReport) {
      app.showActionNotification("Action Disallowed", "You cannot delete a charter report", "default");
      return;
    }

    // strip the reportType from the id ready to send to the API
    const reportIds = ids.map((id) => Number(id.slice(0, -1)));
    document.body.style.cursor = 'wait';

    fetch(`Reports/StatusReports`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(reportIds),
    })
      .then(() => {
        const updatedReportTableContent = reportTableContent.filter((report) => !ids.includes(report.reportId));
        setReportTableContent(updatedReportTableContent);
        app.showActionNotification("Success", "Report Deleted", "success");
        selectNextReport(updatedReportTableContent);
      })
      .catch((error) => {
        app.showActionNotification("Error", `Error deleting report: ${error.message}`, "destructive");
      })
      .finally(() => {
        document.body.style.cursor = 'default';
      });
  }

  if (isMobile) {
    return (
      <Card className="p-2 m-2" style={{ zoom: zoomLevel }}>
        <div className="flex flex-col items-center p-2 m-2">
          <div className="flex flex-row w-min mx-auto mb-2">
            <h1 className="text-4xl font-bold">Reports</h1>
            <div className="justify-end ml-2 self-end my-auto hover:cursor-pointer" onClick={() => setShowTableFilters(!showTableFilters)}>
              <ShowFiltersIcon className='h-7 w-7' />
            </div>
          </div>
          <DataTableWrapper
            columns={getReportColumns(
              (ids: string[]) => confirmDeleteReports(ids)
            )}
            data={reportTableContent}
            setData={setReportTableContent}
            loading={loading}
            viewOnly={app.authLevel < 4} // viewer can see but not edit
            columnVisibility={loadColumnVisibility()}
            groupActions={app.authLevel > 3 ? groupActions : undefined}
            table_title="Reports"
            columnDisplayNames={columnDisplayNames}
            showFilters={showTableFilters}
            onRowClick={(row) => handleReportClick(row.original.reportId, row.original.reportType)}
          />
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="flex flex-col max-w-fit">
            <DialogHeader>
              <DialogTitle>{dialogTitle}</DialogTitle>
            </DialogHeader>
            {dialogContent}
          </DialogContent>
        </Dialog>
      </Card>
    );
  }

  return (
    <Card className="flex flex-col items-center p-2 m-2 h-[95%]" style={{ zoom: zoomLevel }}>
      <div className="flex flex-row justify-between py-4 w-full h-full">
        <div className="flex flex-col items-center p-2 m-2 w-2/5">
          <div className="flex flex-row w-min mx-auto mb-2">
            <h1>Reports</h1>
            <div className="justify-end ml-2 self-end my-auto hover:cursor-pointer" onClick={() => setShowTableFilters(!showTableFilters)}>
              <ShowFiltersIcon className='h-4 w-4' />
            </div>
          </div>
          <DataTableWrapper
            columns={getReportColumns(
              (ids: string[]) => confirmDeleteReports(ids)
            )}
            data={reportTableContent}
            setData={setReportTableContent}
            loading={loading}
            viewOnly={app.authLevel < 4} // viewer can see but not edit
            columnVisibility={loadColumnVisibility()}
            groupActions={app.authLevel > 3 ? groupActions : undefined}
            table_title="Reports"
            columnDisplayNames={columnDisplayNames}
            showFilters={showTableFilters}
            onRowClick={(row) => handleReportClick(row.original.reportId, row.original.reportType)}
          />
        </div>
        <div className="flex flex-col items-center p-2 m-2 h-full w-full">
          <PDFViewer style={{ width: "100%", height: "100%" }}>
            {selectedReportType === "status" && statusReportData ? (
              <StatusReportPDF reportData={statusReportData} projectType={app.selectedProject?.projectTypeId!} />
            ) : selectedReportType === "charter" && charterReportData ? (
              <CharterReportPDF reportData={charterReportData} projectTypeId={app.selectedProject?.projectTypeId!} />
            ) : (
              <div className="flex items-center justify-center h-full">
                <p>Select a report to view</p>
              </div>
            )}
          </PDFViewer>
        </div>
      </div>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="flex flex-col max-w-fit">
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
          </DialogHeader>
          {dialogContent}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default Reports;